import type { Locale, SectorDefinition, Theme } from '../types';
import { chrome } from '../i18n';
import { currentUserName } from '../core/auth';
import { icon } from './Icon';

/**
 * System-wide top bar — identical on every page: identity, navigation
 * (National + Commitments + sectors), the last-updated chip, and the GLOBAL
 * controls (print, theme, language). `activeId` is 'national' | 'commitments'
 * | a sector id (a sector tab stays active on its sub-domain pages too).
 */
export function PlatformBar(
  sectors: SectorDefinition[],
  activeId: string,
  locale: Locale,
  theme: Theme,
  hasCommitments = false,
  updated = '',
): string {
  const c = chrome(locale);

  const link = (id: string, ic: string, label: string) => {
    const active = id === activeId;
    return `<a class="${active ? 'active' : ''}" ${active ? 'aria-current="page"' : ''} href="#/${id}" title="${label}">${icon(ic, 'icon icon-nav')}<span>${label}</span></a>`;
  };

  const nav = [
    link('national', 'gauge', c.navNational),
    ...(hasCommitments ? [link('commitments', 'flag', c.navCommitments)] : []),
    ...sectors.map((s) => link(s.id, s.icon, s.content[locale].navLabel)),
  ].join('');

  return `
  <header class="platform-bar">
    <div class="plat-brand">
      <div class="logo plat-logo">${icon('gauge', 'icon icon-logo')}</div>
      <div>
        <div class="plat-name">${c.platformName}</div>
        <div class="plat-tag">${c.platformTagline}</div>
      </div>
    </div>
    <nav class="nav" aria-label="${c.platformName}">${nav}</nav>
    <div class="plat-actions">
      ${updated ? `<div class="chip" title="${c.updated} ${updated}"><span class="dot" aria-hidden="true"></span><span class="live">${c.updated} ${updated}</span></div>` : ''}
      ${currentUserName(locale) ? `<button class="iconbtn" id="logoutBtn" title="${c.signOut} · ${currentUserName(locale)}" aria-label="${c.signOut}">${icon('log-out')}</button>` : ''}
      <a class="iconbtn ${activeId === 'methodology' ? 'on' : ''}" href="#/methodology" title="${c.navMethodology}" aria-label="${c.navMethodology}">${icon('book-open')}</a>
      <button class="iconbtn" id="printBtn" title="${c.printBriefing}" aria-label="${c.printBriefing}">${icon('printer')}</button>
      <button class="iconbtn" id="themeBtn" title="${theme === 'dark' ? c.themeLight : c.themeDark}" aria-label="${theme === 'dark' ? c.themeLight : c.themeDark}">
        ${icon(theme === 'dark' ? 'sun' : 'moon')}
      </button>
      <div class="switch">
        <div class="toggle" role="group" aria-label="${c.langLabel}">
          <button data-lang="ar" class="${locale === 'ar' ? 'on' : ''}" lang="ar">العربية</button>
          <button data-lang="en" class="${locale === 'en' ? 'on' : ''}" lang="en">English</button>
        </div>
        <span class="admin">${icon('lock', 'icon icon-xs')} ${c.adminNote}</span>
      </div>
    </div>
  </header>`;
}
